import { type InferResponseType, unwrap } from "@nexxt/api-client";
import type { GetPriceSetupHistoryInput } from "@nexxt/contracts/catalog";

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the Price Setup (pattern.md §10): the three tabs of a season, and what the screen offers.

const priceSetup = api.catalog["price-setup"];
const intercompanyPrices = api.catalog["intercompany-prices"];

type PriceSetup = InferResponseType<typeof priceSetup.$get, 200>;
/** A brand with its intercompany rule, null without one (ADR-249). */
export type IntercompanyBrand = InferResponseType<typeof intercompanyPrices.$get, 200>["brands"][number];
export type FinanceSetup = NonNullable<PriceSetup["finance"]>;
export type OperationsSetup = PriceSetup["operations"][number];
export type SalesSetup = PriceSetup["sales"][number];
export type PriceSetupOptions = InferResponseType<typeof priceSetup.options.$get, 200>;

/** The tab whose history is read: Finance's of a season, the others of a brand and price list. */
export type SetupHistoryKey = Pick<GetPriceSetupHistoryInput, "season" | "tab" | "brandOrgId" | "priceList">;

const keys = {
  all: ["price-setup"] as const,
};

export const priceSetupQueries = {
  ...keys,

  /** Seasons, brands and price lists (ADR-048): they change rarely. */
  options: () =>
    queryOptions({
      queryKey: [...keys.all, "options"],
      queryFn: ({ signal }) => unwrap(priceSetup.options.$get({}, { init: { signal } })),
      staleTime: 10 * 60_000,
    }),

  /** The tabs of a season: Finance's, and the ones of a brand by price list. */
  setup: (season: string, brand: string | undefined) =>
    queryOptions({
      queryKey: [...keys.all, "setup", season, brand],
      queryFn: ({ signal }) =>
        unwrap(priceSetup.$get({ query: { season, ...(brand && { brand }) } }, { init: { signal } })),
    }),

  /** Every brand with its intercompany rule (ADR-249): the same in every season. */
  intercompany: () =>
    queryOptions({
      queryKey: [...keys.all, "intercompany"],
      queryFn: ({ signal }) => unwrap(intercompanyPrices.$get({}, { init: { signal } })),
    }),

  /** Who changed and confirmed a tab, newest first (FR-M3-02). */
  history: ({ season, ...query }: SetupHistoryKey) =>
    infiniteQueryOptions({
      queryKey: [...keys.all, "history", season, query],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          priceSetup[":season"].history.$get(
            { param: { season }, query: { ...query, ...(pageParam && { cursor: pageParam }) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: "",
      getNextPageParam: (last) => last.nextCursor ?? undefined,
    }),
};
